import React from "react";
import PropTypes from "prop-types";
import * as Types from "types";
import { compose } from "recompose";
import { lpForm } from "lp-form";
import { Field } from "redux-form";
import { Input, Checkbox, SubmitButton, Spinner, Select } from "lp-components";
import { noop } from "lodash";
import { Terms, RecaptchaInput } from "components";
import { GpiForm, GpiSubmit } from "components/Gpi";
import { COUNTRY_OPTIONS } from "utils/countryOptions";

const propTypes = {
  skipPaymentDetails: PropTypes.bool,
  submitting: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  cart: Types.cart.isRequired,
  change: PropTypes.func.isRequired,
  recaptchaSiteKey: PropTypes.string.isRequired,
};

const defaultProps = {
  skipPaymentDetails: false,
  showTermsModal: noop
};

const validateRecaptcha = (value) => {
  // Environment variable to disable reCAPTCHA validation
  if (process.env.DISABLE_RECAPTCHA === 'true') {
    return undefined // Skip validation if disabled via env var
  }
  // Value will subsequently be validated server-side during order submission
  if (!value) return 'reCAPTCHA must be verified'
}


function BillingForm(
    {
      skipPaymentDetails,
      submitting,
      handleSubmit,
      cart,
      change,
      recaptchaSiteKey
    }) {
  return (
      <div className="billing-form-wrapper">
        {submitting && (
          <div className="billing-form-loading">
            <Spinner />
            <p>Processing payment...</p>
          </div>
        )}
        <form
          className={submitting ? 'form-submitting' : ''}
          onSubmit={(e, vals) => {
            e.preventDefault()
            change('donationAmount', cart.donationAmount)
            handleSubmit(vals)
          }}>
        <div className="cc-form">
          <h1 className="h4 billing-header">
            {skipPaymentDetails ? "Contact Details" : "Billing Details"}
          </h1>
          <div className="billing-details">
            <div className="billing-form">
              <Field
                name="firstName"
                component={Input}
                className="input-short input-short"
                label={
                  skipPaymentDetails
                      ? "First Name"
                      : "First Name (as shown on card)"
                }
                required
                requiredIndicator="*"
              />
              <Field
                  name="lastName"
                  component={Input}
                  className="input-short input-short-right"
                  label={
                    skipPaymentDetails
                        ? "Last Name"
                        : "Last Name (as shown on card)"
                  }
                  required
                  requiredIndicator="*"
              />
              <p className="required-field" style={{ fontSize: '14px', textAlign: 'center' }}>
                Please use billing address on file with your card issuer
              </p>
              <Field
                name="streetAddress1"
                component={Input}
                label="Street Address"
                required
                requiredIndicator="*"
              />
              <Field
                name="streetAddress2"
                component={Input}
                label="Apartment, suite, etc. (optional)"
              />
              <Field
                name="city"
                component={Input}
                className="input-short input-short"
                label="City"
                required
                requiredIndicator="*"
              />
              <Field
                name="state"
                component={Input}
                className="input-short input-short-right"
                label="State/Province"
                required
                requiredIndicator="*"
              />
              <Field
                name="zip"
                component={Input}
                className="input-short input-short"
                label="Postal Code"
                required
                requiredIndicator="*"
              />
              <Field
                name="country"
                component={Select}
                className="input-short input-short-right"
                label="Country"
                required
                requiredIndicator="*"
                options={COUNTRY_OPTIONS}
                enablePlaceholderOption
                placeholder="Select Country"
              />
              {!skipPaymentDetails && <GpiForm />}
              <Field
                  name="email"
                  component={Input}
                  label="Email (confirmation will be sent to this address)"
                  required
                  requiredIndicator="*"
              />
              <Field
                  name="phone"
                  component={Input}
                  label="Phone"
                  required
                  requiredIndicator="*"
              />
              <p className="required-field">* denotes a required field</p>
            </div>
          </div>
          <div className="checkbox terms">
            <Field
                name="termsAgreed"
                component={Checkbox}
                labelComponent={Terms}
                required
            />
          </div>
          {process.env.DISABLE_RECAPTCHA === 'true' ? (
            <input
                type="hidden"
                name="recaptcha"
                value="disabled"
            />
          ) : (
            <Field
                name="recaptcha"
                label={false}
                component={RecaptchaInput}
                recaptchaSiteKey={recaptchaSiteKey}
                validate={validateRecaptcha}
            />
          )}
          <hr/>
          {skipPaymentDetails ? (
            <SubmitButton submitting={submitting}>Submit Order</SubmitButton>
          ) : (
            <GpiSubmit />
          )}
        </div>
      </form>
    </div>
  );
}

BillingForm.propTypes = propTypes;
BillingForm.defaultProps = defaultProps;

export default compose(
    lpForm({
      name: "billing-form",
      constraints: {
        firstName: {
          presence: true,
          length: {maximum: 29},
        },
        lastName: {
          presence: true,
          length: {maximum: 30},
        },
        email: {
          presence: true,
          email: true,
          length: {maximum: 60},
        },
        phone: {
          presence: true,
          length: {maximum: 20},
        },
        streetAddress1: {
          presence: true,
          length: {maximum: 60},
        },
        streetAddress2: {
          length: {maximum: 60},
        },
        city: {
          presence: true,
          length: {maximum: 50},
        },
        state: {
          presence: true,
          length: {maximum: 20},
        },
        zip: {
          presence: true,
          length: {maximum: 15},
        },
        country: {
          presence: true,
          length: {maximum: 60},
        },
        termsAgreed: {
          presence: {
            message: "^You must accept the terms and conditions",
          },
          inclusion: {
            within: [true],
            message: "^You must accept the terms and conditions",
          },
        },
      },
      submitOnChange: false,
    }),
)(BillingForm);

